"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlPoint2dGraphicsComponent = void 0;
const tslib_1 = require("tslib");
const i_gl_indexed_point2d_binder_1 = require("../indexed-point-2d/i-gl-indexed-point2d-binder");
const rc_js_util_1 = require("rc-js-util");
const core_1 = require("@visualization-tools/core");
/**
 * @public
 * Draws points of varying sizes and colors.
 */
class GlPoint2dGraphicsComponent {
    constructor(cameraBinder, indexedBinder) {
        this.cameraBinder = cameraBinder;
        this.indexedBinder = indexedBinder;
        this.type = core_1.EGraphicsComponentType.Entity;
        this.outerVertices = 40;
        DEBUG_MODE && rc_js_util_1._Debug.runBlock(() => {
            (0, core_1.assertBinder)(indexedBinder, i_gl_indexed_point2d_binder_1.IndexedPoint2dIdentifier);
            rc_js_util_1._Debug.assert(indexedBinder.pointsBound === 1, "requires 1 point to be bound");
        });
        this.specification = this.getSpec();
        this.bindings = this.getBindings();
        this.transform = core_1.GlTransformProvider.createOne(this, this.indexedBinder, (updateArg) => updateArg.userTransform, (entity) => entity);
    }
    getCacheId() {
        return [
            "Point",
            this.cameraBinder.getBinderId(),
            this.indexedBinder.getBinderId(),
        ].join("_");
    }
    getLinkableBinders() {
        return [this.indexedBinder];
    }
    initialize(componentRenderer) {
        this.indexedBinder.initialize(componentRenderer);
        this.cameraBinder.initialize(componentRenderer);
        this.bindings.circleGeometryAttribute.initialize(componentRenderer);
        this.bindings.canvasToClipSizeUniform.initialize(componentRenderer);
    }
    onBeforeUpdate(componentRenderer, updateArg) {
        this.bindings.circleGeometryAttribute.bindArray(componentRenderer);
        const frameId = componentRenderer.sharedState.frameCounter;
        this.bindings.canvasToClipSizeUniform.setData(updateArg.canvasDimensions.pixelToClipSize, frameId);
        this.bindings.canvasToClipSizeUniform.bind(componentRenderer);
    }
    update(entity, componentRenderer, updateArg) {
        this.indexedBinder.updateInstanced(entity, componentRenderer, 1);
        this.indexedBinder.overrideColors(componentRenderer, entity, entity.changeId);
        this.cameraBinder.update(this.cameraBinder.getBinderData(entity, updateArg, componentRenderer), componentRenderer);
        // draw
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_FAN, 0, this.outerVertices + 1, entity.data.getLength());
    }
    static generateCircleGeometry(vertexCount) {
        // using index therefore not 2 pi
        const increment = Math.PI / (vertexCount - 1);
        const end = vertexCount * 2 + 2;
        const vertexes = new Float32Array(end);
        for (let i = 2; i < end; i += 2) {
            const step = increment * i;
            vertexes[i] = Math.cos(step);
            vertexes[i + 1] = Math.sin(step);
        }
        return vertexes;
    }
    getSpec() {
        return core_1.GlProgramSpecification.mergeProgramSpecifications([
            this.cameraBinder.specification,
            this.indexedBinder.specification,
            new core_1.GlProgramSpecification(core_1.GlShader.combineShaders([
                core_1.mat2MultiplyValueShader,
                vertexShaderPartial,
            ]), fragmentShaderSource, ["ANGLE_instanced_arrays"]),
        ]);
    }
    getBindings() {
        const geometry = GlPoint2dGraphicsComponent.generateCircleGeometry(this.outerVertices);
        return {
            circleGeometryAttribute: new core_1.GlFloatAttribute("pointGc_position", new core_1.GlFloatBuffer(geometry), 2),
            canvasToClipSizeUniform: new core_1.GlMat3Uniform("pointGc_canvasToClipSize", new rc_js_util_1.Mat3.f32()),
        };
    }
}
(0, tslib_1.__decorate)([
    rc_js_util_1.Once
], GlPoint2dGraphicsComponent.prototype, "getCacheId", null);
exports.GlPoint2dGraphicsComponent = GlPoint2dGraphicsComponent;
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define ATTRIBUTE in \n #define VARYING out \n #else \n #define ATTRIBUTE attribute \n #define VARYING varying \n #endif"
const vertexShaderPartial = new core_1.GlShader(
// @formatter:off
`
ATTRIBUTE lowp vec2 pointGc_position;
uniform highp mat3 pointGc_canvasToClipSize;
VARYING lowp vec4 pointGc_color;

vec2 pointGc_getOffset()
{
    return mat3MultiplyVec2(pointGc_canvasToClipSize, pointGc_position * pointConnector_getSize()) * 0.5;
}

void main()
{
    vec3 position = v_2dCamera_getClipspaceCoords2d(pointConnector_getPosition());
    gl_Position = vec4(pointGc_getOffset() + position.xy, position.z, 1);
    pointGc_color = pointConnector_getColor();
}
`);
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define VARYING in \n #define TEXTURE2D texture \n #else \n #define VARYING varying \n #define TEXTURE2D texture2D \n #endif \n void setFragmentColor(in lowp vec4 color);"
const fragmentShaderSource = new core_1.GlShader(
// @formatter:off
`
VARYING highp vec4 pointGc_color;

void main()
{
    setFragmentColor(pointGc_color);
}
`);
//# sourceMappingURL=gl-point-2d-graphics-component.js.map